import './custom-style-btn.less';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Popover } from '@meitu/saas-ui';
import { getDefaultBrushAttribute } from '@meitu/saas-editor-core/es/layers/brush-layer';
import { BrushType } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { useLocaleStore } from '@/i18n';
import { BrushTagType } from '../config';
import { customConfig } from './custom-config';
import RenderCurvePopover from './render-curve-popover';
import RenderLinePopover from './render-line-popover';

const CustomStyleBtn: React.FC<{
    hiddenTitle?: boolean;
    type: BrushTagType;
    activeId: string;
    popoverId: string;
    onPopover?: (id: string) => void;
    onClick?: (id: string, item: any, type: BrushTagType, disableToggle?: boolean, trackCode?: string) => void;
    onChange?: (item: any, type: BrushTagType) => void;
    trackCode?: string;
}> = React.memo(({ hiddenTitle, onChange, onClick, activeId, onPopover, type, popoverId, trackCode }) => {
    const { $t } = useLocaleStore();

    let popoverContent: React.ReactNode = null;

    const onCloseHandle = useCallback(() => {
        onPopover?.('');
    }, []);

    const onCompleteHandle = useCallback((t: BrushTagType) => {
        const data = getPenData(t);
        onChange?.(data, t);
    }, []);

    // 获取画笔属性
    function getPenData(t: string) {
        if (t === BrushTagType.line) {
            if (!customConfig.line.startPoint.url && !customConfig.line.endPoint.url) {
                // 绘制直线
                return {
                    custom: true,
                    brushType: BrushType.Line,
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: customConfig.line.color,
                            width: customConfig.line.width,
                            linecap: customConfig.line.linecap,
                            dasharray: customConfig.line.dasharray,
                        },
                    ],
                };
            } else {
                // 绘制箭头
                return {
                    custom: true,
                    brushType: BrushType.Arrow,
                    fills: [
                        {
                            ...getDefaultBrushAttribute(),
                            color: customConfig.line.color,
                            width: customConfig.line.width,
                            linecap: customConfig.line.linecap,
                            dasharray: customConfig.line.dasharray,
                        },
                    ],
                    startPoint: { ...customConfig.line.startPoint },
                    endPoint: { ...customConfig.line.endPoint },
                };
            }
        } else if (t === BrushTagType.curve) {
            // 绘制曲线
            return {
                custom: true,
                brushType: BrushType.Curve,
                stroke: { ...customConfig.curve.stroke },
                fills: [{ ...customConfig.curve.fill }],
            };
        }

        return {};
    }

    switch (type) {
        case BrushTagType.line:
            popoverContent = <RenderLinePopover type={type} onClose={onCloseHandle} onComplete={onCompleteHandle} />;
            break;
        case BrushTagType.curve:
            popoverContent = <RenderCurvePopover type={type} onClose={onCloseHandle} onComplete={onCompleteHandle} />;
            break;
    }
    const timer = useRef<any>(0);

    useEffect(() => {
        clearTimeout(timer.current);
        if (popoverId === type) {
            timer.current = setTimeout(() => {
                setIsOpenPopover(true);
            }, 500);
        } else {
            setIsOpenPopover(false);
        }
    }, [popoverId, type]);

    const [isOpenPopover, setIsOpenPopover] = useState(false);

    return (
        <div className="c-brush-collapse__custom-btn">
            <div
                className={classNames('c-brush-collapse__custom-btn-content', {
                    active: activeId === type,
                })}
                onClick={() => {
                    const data = getPenData(type);
                    onClick?.(type, data, type, false, trackCode);
                }}
            >
                <div className="c-brush-collapse__item-wrap">
                    {$t((v) => v.collapse.brushCollapse.custom)}
                    <div
                        className={classNames('c-brush-collapse__custom-btn-edit', { disable: popoverId === type })}
                        onClick={(e) => {
                            e.stopPropagation();
                            const data = getPenData(type);
                            onClick?.(type, data, type, true, trackCode);
                            if (popoverId !== type) {
                                onPopover?.(type);
                            } else if (popoverId && popoverId === type) {
                                onPopover?.('');
                            } else if (!popoverId) {
                                onPopover?.(type);
                            }
                        }}
                    >
                        <IconFont name="EditBold" fontSize={hiddenTitle ? 16 : 12} />
                    </div>
                </div>
            </div>
            <Popover
                placement="bottom"
                content={popoverContent}
                overlayClassName={classNames('c-brush-collapse__custom-btn-popover', { isOpenPopover })}
                destroyTooltipOnHide
                open={popoverId === type && activeId === type}
            >
                <div className="c-brush-collapse__custom-btn-anchor"></div>
            </Popover>
        </div>
    );
});

export default CustomStyleBtn;
